/**
 * Membership shared by roles and groups. Membership lives on the member
 * (`role_ids` / `group_ids`) so there is one source of truth; a role or a group
 * is "its members" = the non-removed members that reference it.
 */
import {
  envelope,
  errorEnvelope,
  notFoundEnvelope,
  paginate,
  sortItems,
} from '../shared/utils/mockServiceHelpers.js';
import { decorateMember } from './membersService.js';
import { store } from './seeds/store.js';

export const membersOf = (key, id) =>
  store.members.filter((m) => m.status !== 'removed' && m[key].includes(id));

export const previewOf = (key, id, limit = 3) =>
  membersOf(key, id)
    .slice(0, limit)
    .map((m) => ({ id: m.id, name: m.name || m.email, email: m.email }));

/** Replace the full member set of one role/group (used on create/update). */
export const setMembership = (key, id, member_ids) => {
  const wanted = new Set(member_ids);
  store.members = store.members.map((m) => {
    const has = m[key].includes(id);
    if (wanted.has(m.id) && !has) return { ...m, [key]: [...m[key], id] };
    if (!wanted.has(m.id) && has) return { ...m, [key]: m[key].filter((x) => x !== id) };
    return m;
  });
};

export const dropFromAllMembers = (key, id) => {
  store.members = store.members.map((m) =>
    m[key].includes(id) ? { ...m, [key]: m[key].filter((x) => x !== id) } : m
  );
};

export const unknownMemberIds = (ids) =>
  ids.filter((id) => !store.members.some((m) => m.id === id));

export const listMembership = (key, id, params = {}) => {
  const { page = 1, page_size = 10, order_by = 'name', order_direction = 'asc' } = params;
  const sorted = sortItems(
    membersOf(key, id),
    (m) => (order_by === 'last_active_at' ? m.last_active_at : m.name || m.email),
    order_direction
  );
  const { items, meta } = paginate(sorted, { page, page_size });
  return envelope({ items: items.map(decorateMember), ...meta }, meta);
};

export const addMembership = (key, id, member_ids = []) => {
  const missing = unknownMemberIds(member_ids);
  if (missing.length)
    return errorEnvelope(422, 'UNKNOWN_MEMBER', `Unknown member: ${missing.join(', ')}`);
  const current = store.members.filter((m) => m[key].includes(id)).map((m) => m.id);
  setMembership(key, id, [...new Set([...current, ...member_ids])]);
  return envelope({ id, member_count: membersOf(key, id).length });
};

export const removeMembership = (key, id, member_id) => {
  const member = store.members.find((m) => m.id === member_id && m[key].includes(id));
  if (!member) return notFoundEnvelope('Member is not in this role or group');
  setMembership(
    key,
    id,
    store.members.filter((m) => m[key].includes(id) && m.id !== member_id).map((m) => m.id)
  );
  return envelope({ id, member_count: membersOf(key, id).length });
};
